import React, { Fragment } from 'react';
import { useRouter } from 'next/router';
import { VendorFilters } from '@/lib/collections/vendor/vendor-search-helpers';
import { X } from '@/public/icons/account/icons';
import FilterGroup from './FilterGroup';
import PriceRangeFilter from './PriceRangeFilter';
import { SideBarFiltersProps } from './types';

const UNWANTED_BRANDS = ['?', 'Cobalt', 'OPPO'];

export default function SidebarFilters(props: SideBarFiltersProps) {
  const router = useRouter();
  const handle = router.query.handle as string;
  const {
    options,
    priceRange,
    handleFilterChange,
    searchParams,
    clearAllFilters,
    shouldShowClearAll,
    isLoading,
    fixedVariantFilters,
    handlePriceRangeChange,
    allVisibleFiltersSelected,
    lastAllFiltersUnselected,
    categoryPk,
  } = props;

  const filterPriority: Partial<Record<string, number>> = {
    categories: 1,
    brand: 2,
  };

  const maxPriority =
    Math.max(
      ...Object.values(filterPriority).filter(
        (value): value is number => value !== undefined,
      ),
    ) + 1;

  return (
    <div className="group grid divide-y" key={handle}>
      {shouldShowClearAll && (
        <div className="p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base text-gray-700">Filter By</h3>
            <button
              onClick={() => clearAllFilters()}
              className="h-8 bg-gray-200 px-4 text-xs"
            >
              Clear All
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.keys(searchParams.variantFilters).map((filter) => {
              const shouldShowOption = options?.[filter]?.length;

              if (shouldShowOption) {
                return (
                  <Fragment key={filter}>
                    {searchParams.variantFilters?.[filter]?.map(
                      (option: string) => {
                        // We don't want the user to remove Fixed Filters
                        if (fixedVariantFilters?.[filter]?.includes(option))
                          return null;

                        const label =
                          options[filter]?.find((o) => o.key === option)
                            ?.label ?? option;

                        return (
                          <button
                            key={option}
                            className="flex text-xs"
                            onClick={() =>
                              handleFilterChange(filter, option, false)
                            }
                            disabled={isLoading}
                          >
                            <div className="rounded-l-full bg-gray-200 px-3 py-1.5">
                              {label}
                            </div>
                            <div className="flex h-full items-center justify-center rounded-r-full bg-gray-700 px-2 text-white">
                              <X className="h-3 w-3" />
                            </div>
                          </button>
                        );
                      },
                    )}
                  </Fragment>
                );
              }

              return null;
            })}
          </div>
        </div>
      )}
      <div className="px-4 pt-4">
        <PriceRangeFilter
          range={priceRange}
          values={{
            max: searchParams.maxPrice,
            min: searchParams.minPrice,
          }}
          onChange={handlePriceRangeChange}
          isLoading={isLoading}
        />
      </div>
      <div className="divide-y">
        {Object.keys(options)
          .sort(
            (a, b) =>
              (filterPriority[a] ?? maxPriority) -
              (filterPriority[b] ?? maxPriority),
          ) // Sort Filters By Priority
          .map((key) => {
            const filter = options[key as VendorFilters]?.filter(
              (o) => !UNWANTED_BRANDS.includes(o.key),
            );

            if (filter != null && filter.length > 1) {
              return (
                <FilterGroup
                  name={key as VendorFilters}
                  key={key}
                  values={filter}
                  handleFilterChange={handleFilterChange}
                  variantFilters={searchParams.variantFilters}
                  fixedVariantFilters={fixedVariantFilters}
                  allVisibleFiltersSelected={allVisibleFiltersSelected}
                  lastAllFiltersUnselected={lastAllFiltersUnselected}
                  isLoading={isLoading}
                  categoryPk={categoryPk || 'default'}
                />
              );
            }

            return null;
          })}
      </div>
    </div>
  );
}
